/**
 * Hook to build GeoJSON for knowledge map
 *
 * Handles async GeoJSON loading and memoization to prevent unnecessary rebuilds
 */

import { useState, useEffect, useRef } from "react";
import type { FeatureCollection, GeoJsonProperties, Geometry } from "geojson";
import type { KnowledgeState } from "@/db/types";
import { buildKnowledgeMap, deduplicateKnowledgeStates } from "@/utils/knowledgeMapUtils";

export function useKnowledgeMapGeoJSON(
  knowledgeStates: KnowledgeState[] | Array<any> | undefined,
  mode: 'combined' | string = 'combined'
): {
  geoJSON: FeatureCollection<Geometry, GeoJsonProperties> | null;
  isLoading: boolean;
} {
  const [geoJSON, setGeoJSON] = useState<FeatureCollection<Geometry, GeoJsonProperties> | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Track previous data to prevent unnecessary rebuilds
  const prevDataRef = useRef<string>("");
  const abortRef = useRef<boolean>(false);

  useEffect(() => {
    // No data yet
    if (!knowledgeStates || knowledgeStates.length === 0) {
      setGeoJSON(null);
      setIsLoading(false);
      prevDataRef.current = "";
      return;
    }

    // Create a stable signature of the knowledge data (include mode to trigger rebuild on tab change)
    const dataSignature = `${mode}|${knowledgeStates.map((k) =>
      `${k.region}:${k.category}:${k.granularityLevel}:${k.completionRate}:${k.totalPrompts}:${k.lastQuizSignature}`
    ).join("|")}`;

    // Skip if data hasn't changed
    if (dataSignature === prevDataRef.current) {
      return;
    }

    prevDataRef.current = dataSignature;
    abortRef.current = false;
    setIsLoading(true);

    console.log(`🗺️ Building knowledge map [${mode}] with ${knowledgeStates.length} regions`);

    // Deduplicate overlapping quizzes (keep only largest per region at 100%)
    const deduplicated = deduplicateKnowledgeStates(knowledgeStates, mode);
    console.log(`📊 After deduplication: ${deduplicated.length} regions (removed ${knowledgeStates.length - deduplicated.length} overlapping quizzes)`);

    buildKnowledgeMap(deduplicated)
      .then((result) => {
        if (!abortRef.current) {
          console.log(`✅ Knowledge map built with ${result.features.length} features`);
          setGeoJSON(result);
          setIsLoading(false);
        }
      })
      .catch((error) => {
        if (!abortRef.current) {
          console.error("❌ Failed to build knowledge map:", error);
          setIsLoading(false);
        }
      });

    // Cleanup: abort if dependencies change
    return () => {
      abortRef.current = true;
    };
  }, [knowledgeStates, mode]);

  return { geoJSON, isLoading };
}
